"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect } from "react";

import { EMBEDDED_IN_APP_COOKIE } from "@/lib/embeddedInAppCookie";

/**
 * Strips `?embedded_in_app=...` from the URL after the cookie has been set
 * server-side, so the marker doesn't propagate via URL sharing or history.
 * Middleware can't do this without forcing a redirect on every entry; doing
 * it client-side keeps the entry response cacheable and avoids an extra hop.
 *
 * The rewrite goes through the router rather than `history.replaceState`. A
 * native call from this effect runs before the App Router has patched
 * `history`, so the router keeps the old URL as canonical and pushes it back
 * on its next update. That bounce aborts in-flight requests and, on the
 * cached detail routes, leaves full-payload prefetches half-read.
 */
export function EmbeddedInAppUrlCleanup() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!searchParams?.has(EMBEDDED_IN_APP_COOKIE)) {
      return;
    }
    const params = new URLSearchParams(searchParams.toString());
    params.delete(EMBEDDED_IN_APP_COOKIE);
    const query = params.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, {
      scroll: false,
    });
  }, [router, pathname, searchParams]);

  return null;
}
